import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Instagram, Lock, Check, Copy, Download, Link2, QrCode, Eye, EyeOff, CreditCard, Calendar, Truck } from 'lucide-react';
import CheckoutModal from '../components/billing/CheckoutModal';
import SalesBrainPolicies from './settings/SalesBrainPolicies';

// Feb 2026 — small reusable password input with show/hide eye toggle.
// Used by the Team Member create/edit modal so admins can verify what
// they're typing while sending the password to their team member.
function PasswordInput({ value, onChange, testid = 'password-input', placeholder = '', autoComplete = 'new-password' }) {
  const [reveal, setReveal] = React.useState(false);
  return (
    <div className="relative mt-1">
      <input
        type={reveal ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className="w-full pl-3 pr-10 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setReveal((v) => !v)}
        className="absolute inset-y-0 right-0 px-2.5 text-fab-text-muted hover:text-fab-text"
        aria-label={reveal ? 'Hide password' : 'Show password'}
        data-testid={`${testid}-toggle`}
      >
        {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

// Phase 1 (Jun 2026) — seller-facing Payment Terms panel. Writes an independent
// `payment_terms` object {advance, credit, credit_days, cod} on the seller doc.
// Separate from the admin-only Sales Brain Policies system.
function PtToggle({ on, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      data-testid={testid}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 flex-shrink-0 ${on ? 'bg-fab-primary' : 'bg-neutral-300'}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${on ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

function PaymentTermsRow({ icon: Icon, iconBg, title, desc, on, onChange, testid, children }) {
  return (
    <div className="py-4 border-b border-fab-primary-lighter last:border-0">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-fab-text text-[14px]">{title}</div>
          <div className="text-[12px] text-fab-text-muted mt-0.5">{desc}</div>
          {children}
        </div>
        <PtToggle on={on} onChange={onChange} testid={testid} />
      </div>
    </div>
  );
}

function PaymentTermsPanel({ value, onChange, onSave }) {
  const pt = value || {};
  const upd = (k, v) => onChange({ ...pt, [k]: v });
  return (
    <div className="fab-card p-6" data-testid="payment-terms-panel">
      <div className="font-display font-bold text-fab-text text-[17px]">Payment Terms</div>
      <div className="text-[12px] text-fab-text-muted mt-0.5 mb-2">
        Manage the payment options available for your buyers.
      </div>
      <PaymentTermsRow
        icon={CreditCard} iconBg="bg-emerald-50 text-emerald-600"
        title="100% Advance Payment"
        desc="Buyers need to make 100% payment in advance to confirm the order."
        on={!!pt.advance} onChange={(v) => upd('advance', v)} testid="pt-advance-toggle"
      />
      <PaymentTermsRow
        icon={Calendar} iconBg="bg-fab-primary/10 text-fab-primary"
        title="Credit Payment"
        desc="Allow buyers to place orders on credit. Set the number of credit days."
        on={!!pt.credit} onChange={(v) => upd('credit', v)} testid="pt-credit-toggle"
      >
        {pt.credit && (
          <div className="mt-3">
            <label className="block text-[12px] font-semibold text-fab-text mb-1">Credit Days</label>
            <input
              type="number" min="1" max="365"
              data-testid="pt-credit-days-input"
              value={pt.credit_days ?? ''}
              onChange={(e) => upd('credit_days', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
              placeholder="e.g. 30"
            />
          </div>
        )}
      </PaymentTermsRow>
      <PaymentTermsRow
        icon={Truck} iconBg="bg-amber-50 text-amber-600"
        title="Cash on Delivery (COD)"
        desc="Allow buyers to request Cash on Delivery. Subject to seller approval."
        on={!!pt.cod} onChange={(v) => upd('cod', v)} testid="pt-cod-toggle"
      />
      <button onClick={onSave} data-testid="save-payment-btn" className="btn-primary text-[13px] mt-4 w-full">
        Save Payment Details
      </button>
    </div>
  );
}


export default function Settings() {
  const { seller, refresh } = useAuth();
  const [tab, setTab] = useState('business');
  const [form, setForm] = useState({});
  // Sales Brain Policies are an admin-only control surface. Regular sellers
  // never see or configure them; admins (and admin impersonation tabs) do.
  const isImpersonation = typeof window !== 'undefined' && !!sessionStorage.getItem('fab_imp_token');
  const canSeeSalesBrain = !!seller?.is_admin || isImpersonation;

  useEffect(() => { if (seller) setForm(seller); }, [seller]);
  // Deep-link support: /dashboard/settings#sales-brain-policies from Sales Brain dashboard (admin-only)
  useEffect(() => {
    if (!canSeeSalesBrain) return;
    const hash = (window.location.hash || '').replace('#', '');
    if (hash === 'sales-brain-policies') setTab('sales_brain_policies');
    const onHash = () => {
      const h = (window.location.hash || '').replace('#', '');
      if (h === 'sales-brain-policies') setTab('sales_brain_policies');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [canSeeSalesBrain]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, business_address: { ...(f.business_address || {}), [k]: v } }));

  const saveBusiness = async () => {
    await api.put('/sellers/profile', {
      business_name: form.business_name, business_type: form.business_type,
      business_city: form.business_city, gst_number: form.gst_number,
      dispatch_days: parseInt(form.dispatch_days) || 2, support_phone: form.support_phone,
    });
    toast.success('Saved');
    refresh();
  };
  const saveAddress = async () => {
    const a = form.business_address || {};
    if (a.pincode && !/^\d{6}$/.test(String(a.pincode).trim())) {
      toast.error('Enter a valid 6-digit PIN Code');
      return;
    }
    await api.put('/sellers/profile', {
      business_address: {
        line1: a.line1 || '', line2: a.line2 || '', area: a.area || '',
        city: a.city || '', state: a.state || '', pincode: a.pincode || '',
        country: a.country || 'India',
      },
    });
    toast.success('Business address saved');
    refresh();
  };
  const savePayment = async () => {
    const pt = form.payment_terms || {};
    const advance = !!pt.advance, credit = !!pt.credit, cod = !!pt.cod;
    if (!advance && !credit && !cod) {
      toast.error('Enable at least one payment term (Advance, Credit or COD).');
      return;
    }
    let credit_days = null;
    if (credit) {
      credit_days = parseInt(pt.credit_days, 10);
      if (!Number.isInteger(credit_days) || credit_days < 1 || credit_days > 365) {
        toast.error('Enter valid credit days (1–365).');
        return;
      }
    }
    await api.put('/sellers/payment-methods', {
      upi_id: form.upi_id, bank_account: form.bank_account,
      bank_ifsc: form.bank_ifsc, bank_name: form.bank_name, qr_code_url: form.qr_code_url,
      payment_terms: { advance, credit, cod, credit_days },
    });
    toast.success('Payment details saved');
    refresh();
  };
  const saveAI = async () => {
    await api.put('/sellers/ai-config', {
      ai_persona_name: form.ai_persona_name, ai_language: form.ai_language, ai_tone: form.ai_tone,
    });
    toast.success('AI config saved');
    refresh();
  };

  const tabs = [
    ['business', 'Business Profile'],
    ['payment', 'Payment Settings'],
    ['ai', 'AI Settings'],
    ...(canSeeSalesBrain ? [['sales_brain_policies', 'Sales Brain Policies']] : []),
    ['plan', 'Plan & Billing'],
  ];

  return (
    <div className="space-y-5 fade-up max-w-6xl">
      <div className="flex gap-1 border-b border-fab-primary-lighter overflow-x-auto">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-[13px] font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === k ? 'text-fab-primary border-fab-primary' : 'text-fab-text-muted border-transparent'}`}>{l}</button>
        ))}
      </div>

      {tab === 'business' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <div className="fab-card p-6 space-y-3" data-testid="settings-basic-info">
              <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Basic Information</div>
              <Field label="Business name" value={form.business_name} onChange={(v) => set('business_name', v)} />
              <Field label="Business type" value={form.business_type} onChange={(v) => set('business_type', v)} />
              <Field label="City" value={form.business_city} onChange={(v) => set('business_city', v)} />
              <Field label="GST number" value={form.gst_number} onChange={(v) => set('gst_number', v)} />
              <Field label="Dispatch days" type="number" value={form.dispatch_days} onChange={(v) => set('dispatch_days', v)} />
              <Field label="Support phone" value={form.support_phone} onChange={(v) => set('support_phone', v)} />
              <button onClick={saveBusiness} data-testid="save-basic-info-btn" className="btn-primary text-[13px] mt-2">Save</button>
            </div>

            <CustomerConnectSection />
          </div>

          <div className="fab-card p-6 space-y-3" data-testid="settings-business-address">
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Business Address</div>
            <div className="text-[12px] text-fab-text-secondary -mt-1 mb-1">Your official business address — used on invoices.</div>
            <Field label="Address Line 1" value={(form.business_address || {}).line1} onChange={(v) => setAddr('line1', v)} testid="addr-line1" />
            <Field label="Address Line 2 (optional)" value={(form.business_address || {}).line2} onChange={(v) => setAddr('line2', v)} testid="addr-line2" />
            <Field label="Area / Locality" value={(form.business_address || {}).area} onChange={(v) => setAddr('area', v)} testid="addr-area" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="City" value={(form.business_address || {}).city} onChange={(v) => setAddr('city', v)} testid="addr-city" />
              <Field label="State" value={(form.business_address || {}).state} onChange={(v) => setAddr('state', v)} testid="addr-state" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="PIN Code" value={(form.business_address || {}).pincode} onChange={(v) => setAddr('pincode', String(v).replace(/\D/g, '').slice(0, 6))} testid="addr-pincode" />
              <Field label="Country" value={(form.business_address || {}).country || 'India'} onChange={(v) => setAddr('country', v)} testid="addr-country" />
            </div>
            <button onClick={saveAddress} data-testid="save-business-address-btn" className="btn-primary text-[13px] mt-2">Save Address</button>
          </div>

          <SocialMediaSection
            initial={seller?.instagram_url || ''}
            onSaved={refresh}
          />
        </div>
      )}

      {tab === 'payment' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div className="fab-card p-6 space-y-3">
            <Field label="UPI ID" value={form.upi_id} onChange={(v) => set('upi_id', v)} placeholder="ramesh@paytm" />
            <PaymentQRUploader
              value={form.qr_code_url}
              onChange={(v) => set('qr_code_url', v)}
            />
            <Field label="Bank account number" value={form.bank_account} onChange={(v) => set('bank_account', v)} />
            <Field label="IFSC" value={form.bank_ifsc} onChange={(v) => set('bank_ifsc', v)} />
            <Field label="Bank name" value={form.bank_name} onChange={(v) => set('bank_name', v)} />
          </div>

          <PaymentTermsPanel
            value={form.payment_terms || {}}
            onChange={(pt) => set('payment_terms', pt)}
            onSave={savePayment}
          />
        </div>
      )}

      {tab === 'ai' && (
        <div className="space-y-4">
          <div className="fab-card p-6 space-y-3">
            <Field label="AI persona name" value={form.ai_persona_name} onChange={(v) => set('ai_persona_name', v)} placeholder="Priya" />
            <div>
              <label className="text-[12px] font-semibold">Language</label>
              <select value={form.ai_language || 'en'} onChange={(e) => set('ai_language', e.target.value)} className="fab-input mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] bg-white">
                <option value="en">English</option><option value="hi">Hindi</option><option value="gu">Gujarati</option><option value="ta">Tamil</option><option value="bilingual">Bilingual</option>
              </select>
            </div>
            <div>
              <label className="text-[12px] font-semibold">Tone</label>
              <select value={form.ai_tone || 'friendly'} onChange={(e) => set('ai_tone', e.target.value)} className="fab-input mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] bg-white">
                <option value="professional">Professional</option><option value="friendly">Friendly</option><option value="casual">Casual</option>
              </select>
            </div>
            <button onClick={saveAI} className="btn-primary text-[13px] mt-2">Save AI Settings</button>
          </div>

          <TeamMembersSection />
        </div>
      )}

      {tab === 'sales_brain_policies' && canSeeSalesBrain && (
        <SalesBrainPolicies />
      )}

      {tab === 'plan' && (
        <PlanBillingSection />
      )}
    </div>
  );
}

// Feb 2026 — Social Media / Instagram profile link. PRO or ENTERPRISE only
// (or PRO/ENTERPRISE via active admin promotion). Backend also enforces
// the same rule on PUT /api/sellers/profile.
function SocialMediaSection({ initial, onSaved }) {
  const [access, setAccess] = useState(null);
  const [value, setValue] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [savedOk, setSavedOk] = useState(false);

  useEffect(() => {
    api.get('/settings/social-access')
      .then(({ data }) => setAccess(data))
      .catch(() => setAccess({ can_edit: false, plan: 'FREE_TRIAL', promotion: null }));
  }, []);
  useEffect(() => { setValue(initial || ''); }, [initial]);

  const igOk = /^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9_.]{1,30}\/?$/.test(value.trim());

  const save = async () => {
    setErr(''); setSavedOk(false); setSaving(true);
    try {
      await api.put('/sellers/profile', { instagram_url: value.trim() });
      setSavedOk(true);
      toast.success('Instagram link saved');
      onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save Instagram link';
      setErr(msg);
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (!access) return null;

  return (
    <div className="fab-card p-6" data-testid="settings-social-media">
      <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-3">Social Media</div>

      {!access.can_edit ? (
        <div
          className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
          data-testid="settings-instagram-locked"
        >
          <Lock className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-[13px] text-amber-900">
            <div className="font-semibold">Instagram link is available for PRO and ENTERPRISE plans only.</div>
            <div className="text-[12px] text-amber-800 mt-0.5">Current plan: <span className="font-semibold">{access.plan}</span>. Upgrade to add your Instagram profile link.</div>
          </div>
        </div>
      ) : (
        <div>
          <label className="text-[12px] font-semibold text-fab-text flex items-center gap-2">
            <Instagram className="w-4 h-4 text-[#E1306C]" />
            Instagram profile URL
            {access.source === 'promotion' && (
              <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200">
                Promo · {access.plan}
              </span>
            )}
          </label>
          <div className="mt-1 flex gap-2 items-stretch">
            <div className="relative flex-1">
              <input
                type="url"
                value={value}
                onChange={(e) => { setValue(e.target.value); setSavedOk(false); setErr(''); }}
                placeholder="https://instagram.com/ramesh.textiles"
                className="w-full px-3 py-2 pr-9 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
                data-testid="settings-instagram-input"
              />
              {value && igOk && (
                <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" data-testid="settings-instagram-valid" />
              )}
            </div>
            <button
              onClick={save}
              disabled={saving || (value.trim() && !igOk)}
              className="btn-primary text-[13px] px-4 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="settings-instagram-save-btn"
            >{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
          <div className="text-[11px] text-fab-text-muted mt-1.5">
            Just paste your public Instagram profile link. Buyers will see an Instagram icon in your chat header.
          </div>
          {err && <div className="text-[12px] text-red-600 mt-2" data-testid="settings-instagram-error">{err}</div>}
          {savedOk && !err && (
            <div className="text-[12px] text-emerald-700 mt-2" data-testid="settings-instagram-saved">
              Instagram link saved.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, testid }) {
  return (
    <div>
      <label className="text-[12px] font-semibold">{label}</label>
      <input type={type} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="fab-input mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" />
    </div>
  );
}

// Feb 2026 — Team Members management. Reuses /api/team/* endpoints;
// enforces plan cap client-side too (backend is authoritative).
function TeamMembersSection() {
  const [limits, setLimits] = useState(null);
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);

  const load = () => {
    api.get('/team/limits').then(({ data }) => setLimits(data)).catch(() => setLimits({ cap: 0, used: 0 }));
    api.get('/team/members').then(({ data }) => { setMembers(data.items || []); setDepartments(data.departments || []); });
  };
  useEffect(load, []);

  const teamLoginUrl = `${window.location.origin}/team-login`;
  const copyLoginLink = async () => {
    try { await navigator.clipboard.writeText(teamLoginUrl); toast.success('Link copied!'); }
    catch { toast.error('Copy failed'); }
  };
  const shareLoginLink = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'FABTEXS AI — Team Member Login', url: teamLoginUrl }); }
      catch { /* user cancelled */ }
    } else { copyLoginLink(); }
  };

  if (!limits) return null;
  const locked = limits.cap === 0;
  const atMax = limits.used >= limits.cap;

  return (
    <div className="fab-card p-6" data-testid="team-members-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Team Members</div>
          <div className="text-[12.5px] text-fab-text-secondary mt-0.5">
            Manage your team and give each member access only to the departments they need.
          </div>
        </div>
        {!locked && (
          <div className="text-[11px] font-semibold text-fab-text-muted" data-testid="team-used-count">
            {limits.used} of {limits.cap} members used
          </div>
        )}
      </div>

      {locked ? (
        <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="team-locked">
          <div className="text-[13px] text-amber-900">
            <div className="font-semibold">Team member access is available on the Pro plan.</div>
            <a href="/pricing" className="mt-2 inline-block btn-primary text-[12px]">Upgrade to Pro</a>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-3">
            <button
              onClick={() => setAddOpen(true)}
              disabled={atMax}
              className="btn-primary text-[13px] disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="team-add-btn"
            >+ Add Team Member</button>
            {atMax && (
              <div className="text-[11.5px] text-fab-text-muted mt-2" data-testid="team-max-reached">
                Maximum {limits.cap} team members reached for your plan.
              </div>
            )}
          </div>

          {members.length > 0 && (
            <div className="mt-4 space-y-3" data-testid="team-list">
              {members.map((m) => (
                <div key={m.id} className={`border rounded-xl p-3 ${m.active ? 'border-fab-primary-lighter' : 'border-neutral-200 bg-neutral-50'}`} data-testid={`team-member-${m.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-fab-text text-[13.5px]">{m.name}</div>
                      <div className="text-[11.5px] text-fab-text-muted font-mono">{m.phone}</div>
                    </div>
                    <div className={`text-[10.5px] font-semibold inline-flex items-center gap-1 ${m.active ? 'text-emerald-600' : 'text-neutral-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${m.active ? 'bg-emerald-500' : 'bg-neutral-400'}`} /> {m.active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(m.departments || []).map((d) => (
                      <span key={d} className="text-[10.5px] px-2 py-0.5 rounded-full bg-fab-primary-lighter text-fab-primary font-semibold">{d}</span>
                    ))}
                    {(m.departments || []).length === 0 && (
                      <span className="text-[10.5px] text-fab-text-muted italic">No departments assigned</span>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button onClick={() => setEditMember(m)} className="text-[12px] font-semibold text-fab-primary hover:underline" data-testid={`team-edit-${m.id}`}>Edit Access</button>
                    <button onClick={async () => {
                      const pw = window.prompt('Enter a new password for this member (min 6 chars):');
                      if (!pw) return;
                      await api.patch(`/team/members/${m.id}`, { password: pw });
                      toast.success('Password reset');
                    }} className="text-[12px] font-semibold text-fab-text-secondary hover:underline" data-testid={`team-reset-${m.id}`}>Reset Password</button>
                    <button onClick={async () => {
                      if (!window.confirm(`Remove ${m.name} from your team?`)) return;
                      await api.delete(`/team/members/${m.id}`); load();
                      toast.success('Member removed');
                    }} className="text-[12px] font-semibold text-red-600 hover:underline" data-testid={`team-remove-${m.id}`}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 border-t border-fab-primary-lighter pt-4">
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Team Login</div>
            <div className="text-[12px] text-fab-text-secondary mt-0.5">Share this login page with your team members.</div>
            <div className="mt-2 flex gap-2 items-stretch">
              <input readOnly value={teamLoginUrl} onFocus={(e) => e.target.select()}
                className="flex-1 px-3 py-2 rounded-lg border border-fab-primary-lighter bg-fab-elevated text-[12.5px] font-mono truncate"
                data-testid="team-login-url" />
              <button onClick={copyLoginLink} className="btn-primary text-[12.5px]" data-testid="team-login-copy">Copy Link</button>
              <button onClick={shareLoginLink} className="px-3 py-2 rounded-lg border border-fab-primary text-fab-primary text-[12.5px] font-semibold" data-testid="team-login-share">Share</button>
            </div>
          </div>
        </>
      )}

      {addOpen && (
        <TeamMemberModal
          mode="add" departments={departments}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
      {editMember && (
        <TeamMemberModal
          mode="edit" member={editMember} departments={departments}
          onClose={() => setEditMember(null)}
          onSaved={() => { setEditMember(null); load(); }}
        />
      )}
    </div>
  );
}

function TeamMemberModal({ mode, member, departments, onClose, onSaved }) {
  const [name, setName] = useState(member?.name || '');
  const [phone, setPhone] = useState(member?.phone || '');
  const [password, setPassword] = useState('');
  const [deps, setDeps] = useState(member?.departments || []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const toggle = (d) => setDeps((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]);

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      if (mode === 'add') {
        await api.post('/team/members', { name, phone, password, departments: deps });
      } else {
        await api.patch(`/team/members/${member.id}`, { departments: deps, name });
      }
      toast.success(mode === 'add' ? 'Team member added successfully.' : 'Access updated');
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const canSubmit = mode === 'edit' || (name.trim().length >= 1 && phone.trim().length >= 7 && password.length >= 6);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-3 sm:p-0" onClick={onClose} data-testid="team-modal">
      <div className="bg-white w-full sm:max-w-md rounded-2xl shadow-xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-fab-text text-[16px]">{mode === 'add' ? 'Add Team Member' : `Edit Access — ${member?.name}`}</div>
          <button onClick={onClose} className="text-fab-text-muted p-1">✕</button>
        </div>
        <div className="space-y-3">
          {mode === 'add' && (
            <>
              <div>
                <label className="text-[12px] font-semibold">Full Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" data-testid="team-modal-name" />
              </div>
              <div>
                <label className="text-[12px] font-semibold">Mobile Number</label>
                <input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" data-testid="team-modal-phone" />
              </div>
              <div>
                <label className="text-[12px] font-semibold">Password</label>
                <PasswordInput value={password} onChange={setPassword} testid="team-modal-password" />
              </div>
            </>
          )}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[12px] font-semibold">Department Access</label>
              <div className="text-[11px] flex gap-2">
                <button type="button" onClick={() => setDeps([...departments])} className="text-fab-primary font-semibold">Select All</button>
                <button type="button" onClick={() => setDeps([])} className="text-fab-text-muted font-semibold">Clear All</button>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto" data-testid="team-modal-departments">
              {departments.map((d) => {
                const on = deps.includes(d);
                return (
                  <button key={d} type="button" onClick={() => toggle(d)}
                    className={`text-left text-[12px] px-2.5 py-1.5 rounded border ${on ? 'bg-fab-primary text-white border-fab-primary' : 'bg-white text-fab-text border-fab-primary-lighter'}`}
                    data-testid={`team-modal-dep-${d.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >{on ? '✓ ' : ''}{d}</button>
                );
              })}
            </div>
          </div>
          {err && <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-fab-text-secondary text-[13px]">Cancel</button>
          <button onClick={save} disabled={!canSubmit || saving} className="btn-primary text-[13px] disabled:opacity-60" data-testid="team-modal-save">
            {saving ? 'Saving…' : (mode === 'add' ? 'Add Member' : 'Save Access')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Feb 2026 — CUSTOMER CONNECT. Available to every plan; no gating.
// Reads /api/customer-connect/link which lazily creates the seller's slug
// and returns { link, slug, qr_data_url, qr_png_url }.
function CustomerConnectSection() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [qrCopied, setQrCopied] = useState(false);

  useEffect(() => {
    api.get('/customer-connect/link')
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load Customer Connect'));
  }, []);

  const copyLink = async () => {
    if (!data?.link) return;
    try { await navigator.clipboard.writeText(data.link); setLinkCopied(true); toast.success('Link copied!'); setTimeout(() => setLinkCopied(false), 2000); }
    catch { toast.error('Copy failed. Please copy manually.'); }
  };

  const copyQR = async () => {
    if (!data?.qr_data_url) return;
    try {
      // Modern: ClipboardItem with image/png blob. Falls back to copying the
      // link when the browser doesn't support image clipboard writes.
      const res = await fetch(data.qr_data_url);
      const blob = await res.blob();
      if (window.ClipboardItem && navigator.clipboard.write) {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        setQrCopied(true); toast.success('QR copied to clipboard');
        setTimeout(() => setQrCopied(false), 2000);
      } else {
        await navigator.clipboard.writeText(data.link);
        toast.info('QR image copy not supported here — link copied instead.');
      }
    } catch { toast.error('Copy failed. Use Download QR Code instead.'); }
  };

  const downloadQR = () => {
    if (!data?.qr_data_url) return;
    const safe = (data.business_name || 'seller').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '') || 'seller';
    const a = document.createElement('a');
    a.href = data.qr_data_url;
    a.download = `FABTEXS-AI-Customer-QR-${safe}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="fab-card p-6" data-testid="customer-connect-panel">
      <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Customer Connect</div>
      <div className="text-[12.5px] text-fab-text-secondary mt-1">
        Give customers one simple way to connect with your business and receive your latest collection updates.
      </div>

      {err && (
        <div className="mt-4 text-[12.5px] bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">{err}</div>
      )}
      {!data && !err && (
        <div className="mt-6 text-[12.5px] text-fab-text-muted">Loading…</div>
      )}

      {data && (
        <>
          <div className="mt-4">
            <div className="text-[13px] font-semibold text-fab-text flex items-center gap-1.5">
              <Link2 className="w-4 h-4 text-fab-primary" /> Your FABTEXS AI Customer Link
            </div>
            <div className="text-[11.5px] text-fab-text-muted mt-0.5">
              Share this link anywhere to connect customers with your business.
            </div>
            <div className="mt-2 flex gap-2 items-stretch">
              <input
                readOnly
                value={data.link}
                onFocus={(e) => e.target.select()}
                className="flex-1 px-3 py-2 rounded-lg border border-fab-primary-lighter bg-fab-elevated text-[12.5px] font-mono truncate"
                data-testid="customer-connect-link"
              />
              <button
                onClick={copyLink}
                className="px-3 py-2 rounded-lg bg-fab-primary text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
                data-testid="customer-connect-copy-link"
              >{linkCopied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy Link</>}</button>
            </div>
          </div>

          <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wider text-fab-text-muted">
            <div className="flex-1 h-px bg-fab-primary-lighter" />
            <span>OR</span>
            <div className="flex-1 h-px bg-fab-primary-lighter" />
          </div>

          <div>
            <div className="text-[13px] font-semibold text-fab-text flex items-center gap-1.5">
              <QrCode className="w-4 h-4 text-fab-primary" /> Your Customer QR Code
            </div>
            <div className="text-[11.5px] text-fab-text-muted mt-0.5">
              Scan this QR code to connect with your business.
            </div>
            <div className="mt-3 bg-white border border-fab-primary-lighter rounded-xl p-4 flex justify-center">
              <img
                src={data.qr_data_url}
                alt="Customer Connect QR"
                className="w-48 h-48 sm:w-56 sm:h-56 object-contain"
                data-testid="customer-connect-qr"
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={copyQR}
                className="px-3 py-2 rounded-lg border border-fab-primary text-fab-primary text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-fab-primary-lighter"
                data-testid="customer-connect-copy-qr"
              >{qrCopied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy QR Code</>}</button>
              <button
                onClick={downloadQR}
                className="px-3 py-2 rounded-lg bg-fab-primary text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="customer-connect-download-qr"
              ><Download className="w-4 h-4" /> Download QR Code</button>
            </div>
          </div>

          <div className="mt-5 text-[11.5px] text-fab-text-secondary bg-fab-elevated rounded-lg p-3">
            Share this link or QR code on Instagram, social media, messages, packaging, visiting cards, or anywhere you want customers to connect with your business.
          </div>
        </>
      )}
    </div>
  );
}


// Feb 2026 — Plan & Billing tab. Shows current plan + trial state and,
// when a paid subscription exists, its status, cycle and next billing
// date. Sellers can Subscribe, Change Plan, or Cancel-at-cycle-end.
// Backend/webhook is authoritative; this component just reflects state.
function PlanBillingSection() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [checkout, setCheckout] = useState(null); // { tier, cycle } | null
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: h }, { data: inv }] = await Promise.all([
        api.get('/billing/my-subscription'),
        api.get('/billing/history').catch(() => ({ data: { payments: [], subscriptions: [] } })),
        api.get('/billing/invoices').catch(() => ({ data: { invoices: [] } })),
      ]);
      setState(s);
      setHistory(h);
      setInvoices(inv?.invoices || []);
    } catch {
      setState({ plan_tier: 'FREE_TRIAL', is_trial: true, trial_active: false, subscription: null, configured: false });
    }
  };

  const downloadInvoice = async (inv) => {
    setDownloadingId(inv.id);
    try {
      const res = await api.get(`/billing/invoices/${inv.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_no || 'invoice'}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => { load(); }, []);

  const openCheckout = (tier, cycle) => setCheckout({ tier, cycle });
  const closeCheckout = () => setCheckout(null);

  const cancel = async () => {
    if (!window.confirm('Cancel your subscription at the end of the current billing period? You will keep paid access until then.')) return;
    setBusy(true);
    try {
      await api.post('/billing/cancel');
      toast.success('Cancellation scheduled — access continues until period end.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <div className="fab-card p-6 text-fab-text-muted text-[13px]">Loading…</div>;

  const sub = state.subscription;
  const paid = sub && ['active', 'authenticated', 'created', 'pending', 'halted'].includes(sub.status);
  const cycleLabel = sub?.cycle === 'yearly' ? 'Yearly' : sub?.cycle === 'monthly' ? 'Monthly' : '—';
  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
  const statusLabel = (() => {
    if (state.is_trial && state.trial_active) return { text: 'PRO TRIAL · Active', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    if (!sub) {
      if (state.plan_status === 'EXPIRED') return { text: 'Trial expired · Subscribe to continue', tone: 'bg-red-100 text-red-700 border-red-200' };
      return { text: state.plan_status || 'ACTIVE', tone: 'bg-fab-primary-lighter text-fab-primary border-fab-primary-lighter' };
    }
    if (sub.cancel_at_cycle_end) return { text: 'Cancelling at period end', tone: 'bg-amber-100 text-amber-700 border-amber-200' };
    if (sub.status === 'active' || sub.status === 'authenticated') return { text: 'Active', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    if (sub.status === 'halted') return { text: 'Payment issue', tone: 'bg-red-100 text-red-700 border-red-200' };
    if (sub.status === 'pending') return { text: 'Payment pending', tone: 'bg-amber-100 text-amber-700 border-amber-200' };
    if (sub.status === 'cancelled') return { text: 'Cancelled', tone: 'bg-neutral-100 text-neutral-700 border-neutral-200' };
    if (sub.status === 'completed') return { text: 'Completed', tone: 'bg-neutral-100 text-neutral-700 border-neutral-200' };
    return { text: sub.status || '—', tone: 'bg-neutral-100 text-neutral-700 border-neutral-200' };
  })();

  return (
    <div className="space-y-4" data-testid="plan-billing-section">
      <div className="fab-card p-6" data-testid="plan-current-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Current plan</div>
            <div className="font-display text-3xl font-extrabold text-fab-primary mt-1" data-testid="plan-current-tier">
              {state.is_trial && state.trial_active ? 'PRO TRIAL' : (state.plan_tier || 'FREE_TRIAL')}
            </div>
            <div className="mt-1.5 inline-flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusLabel.tone}`} data-testid="plan-status-chip">
                {statusLabel.text}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            {!paid && (
              <button
                onClick={() => openCheckout('PRO', 'monthly')}
                className="btn-primary text-[13px] px-4 py-2 rounded-lg"
                disabled={!state.configured}
                data-testid="plan-subscribe-btn"
              >
                {state.is_trial && state.trial_active ? 'Subscribe now' : 'Choose a plan'}
              </button>
            )}
            {paid && !sub.cancel_at_cycle_end && (
              <>
                <button onClick={() => openCheckout(sub.tier || 'PRO', sub.cycle || 'monthly')} className="text-[12.5px] px-3.5 py-2 rounded-lg border border-fab-primary text-fab-primary font-semibold hover:bg-fab-primary-lighter/40" data-testid="plan-change-btn">
                  Change Plan
                </button>
                <button onClick={cancel} disabled={busy} className="text-[12.5px] px-3.5 py-2 rounded-lg border border-red-300 text-red-600 font-semibold hover:bg-red-50 disabled:opacity-60" data-testid="plan-cancel-btn">
                  Cancel Subscription
                </button>
              </>
            )}
          </div>
        </div>

        {!state.configured && (
          <div className="mt-3 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Razorpay is being configured. Subscribe buttons will activate once setup is complete.
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Billing cycle" value={sub ? cycleLabel : '—'} testid="plan-billing-cycle" />
          <MiniStat label="Current period ends" value={fmt(sub?.current_period_end)} testid="plan-period-end" />
          <MiniStat label="Next billing" value={fmt(sub?.next_billing_at)} testid="plan-next-billing" />
          <MiniStat
            label={state.is_trial ? 'Trial ends' : 'Access valid until'}
            value={fmt(state.is_trial ? state.trial_ends_at : state.plan_ends_at)}
            testid="plan-access-until"
          />
        </div>
      </div>

      {history && history.payments && history.payments.length > 0 && (
        <div className="fab-card p-6" data-testid="plan-history-card">
          <div className="text-[13px] font-semibold text-fab-text mb-3">Billing history</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-fab-primary/5"><tr>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Date</th>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Amount</th>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Method</th>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Status</th>
              </tr></thead>
              <tbody>
                {history.payments.slice(0, 20).map((p, i) => (
                  <tr key={p.razorpay_payment_id || i} className="border-t border-fab-primary-lighter/60">
                    <td className="px-3 py-2">{fmt(p.created_at)}</td>
                    <td className="px-3 py-2">₹{((p.amount || 0) / 100).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 capitalize">{p.method || '—'}</td>
                    <td className="px-3 py-2 capitalize">{p.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="fab-card p-6" data-testid="invoice-history-card">
        <div className="text-[13px] font-semibold text-fab-text mb-3">Invoice History</div>
        {invoices && invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="invoice-history-table">
              <thead className="bg-fab-primary/5"><tr>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Invoice No.</th>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Invoice Date</th>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Plan</th>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Billing Period</th>
                <th className="text-right px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Amount</th>
                <th className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Payment Status</th>
                <th className="text-right px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">Invoice</th>
              </tr></thead>
              <tbody>
                {invoices.map((inv, i) => {
                  const total = inv.gst?.total_payable != null ? inv.gst.total_payable : inv.line_item?.amount;
                  const period = inv.period_start
                    ? `${fmt(inv.period_start)} – ${fmt(inv.period_end)}`
                    : '—';
                  const paid = String(inv.payment_status || '').toUpperCase() === 'PAID';
                  return (
                    <tr key={inv.id || i} className="border-t border-fab-primary-lighter/60" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-3 py-2 font-medium text-fab-text" data-testid={`invoice-no-${inv.id}`}>{inv.invoice_no || '—'}</td>
                      <td className="px-3 py-2">{fmt(inv.invoice_date)}</td>
                      <td className="px-3 py-2">{inv.line_item?.description || `${inv.tier || ''} ${inv.cycle || ''}`.trim() || '—'}</td>
                      <td className="px-3 py-2">{period}</td>
                      <td className="px-3 py-2 text-right font-semibold">₹{Number(total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${paid ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                          {inv.payment_status || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => downloadInvoice(inv)}
                          disabled={downloadingId === inv.id}
                          className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border border-fab-primary text-fab-primary font-semibold hover:bg-fab-primary-lighter/40 disabled:opacity-60"
                          data-testid={`invoice-download-${inv.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          {downloadingId === inv.id ? 'Downloading…' : 'Download'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-[13px] text-fab-text-muted py-6 text-center" data-testid="invoice-empty-state">
            No invoices yet
          </div>
        )}
      </div>

      <CheckoutModal
        open={!!checkout}
        onClose={closeCheckout}
        defaultTier={checkout?.tier || 'PRO'}
        defaultCycle={checkout?.cycle || 'monthly'}
        onSubscribed={load}
      />
    </div>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-fab-primary-lighter p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold">{label}</div>
      <div className="text-[13px] font-semibold text-fab-text mt-0.5" data-testid={testid}>{value}</div>
    </div>
  );
}


// Feb 2026 — Payment QR image uploader. Replaces the freeform text URL
// field with a proper image upload against POST /api/sellers/payment-qr.
// The backend stores the served /api/files/... URL in the SAME
// sellers.qr_code_url field the existing buyer payment flow already reads
// (_payment_instructions), so no buyer-side change is required.
function PaymentQRUploader({ value, onChange }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;
  const displayUrl = value ? (value.startsWith('http') ? value : `${base}${value}`) : '';

  const pickFile = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-selecting the same file later
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a PNG, JPG or WEBP image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('QR image is too large (max 10 MB).');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/sellers/payment-qr', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.qr_code_url);
      toast.success('QR code uploaded');
    } catch (er) {
      toast.error(er?.response?.data?.detail || 'Failed to upload QR');
    } finally { setUploading(false); }
  };

  const remove = async () => {
    setUploading(true);
    try {
      await api.delete('/sellers/payment-qr');
      onChange(null);
      toast.success('QR code removed');
    } catch (er) {
      toast.error(er?.response?.data?.detail || 'Failed to remove QR');
    } finally { setUploading(false); }
  };

  return (
    <div data-testid="payment-qr-uploader">
      <label className="text-[12px] font-semibold text-fab-text">Payment QR Code</label>
      <div className="mt-1 flex items-start gap-3">
        {value ? (
          <div className="flex flex-col items-start gap-1">
            <img
              src={displayUrl}
              alt="Payment QR"
              className="w-24 h-24 rounded-lg object-contain border border-fab-primary-lighter bg-white p-1"
              data-testid="payment-qr-preview"
            />
            <a
              href={displayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-semibold text-fab-primary underline"
              data-testid="payment-qr-view-full"
            >
              View Full QR
            </a>
          </div>
        ) : (
          <div className="w-24 h-24 rounded-lg border border-dashed border-fab-primary-lighter flex items-center justify-center text-fab-text-muted" data-testid="payment-qr-empty">
            <QrCode className="w-8 h-8" />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <div className="text-[12px] text-fab-text-secondary">
            Upload your UPI / payment QR image. PNG, JPG or WEBP, up to 10 MB. Buyers will see this image in the payment step.
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={pickFile}
              disabled={uploading}
              className="px-3 py-1.5 rounded-lg bg-fab-primary text-white text-[12.5px] font-semibold disabled:opacity-60"
              data-testid={value ? 'payment-qr-replace' : 'payment-qr-upload'}
            >
              {uploading ? 'Uploading…' : value ? 'Replace QR' : 'Upload QR'}
            </button>
            {value && (
              <button
                type="button"
                onClick={remove}
                disabled={uploading}
                className="px-3 py-1.5 rounded-lg border border-fab-primary-lighter text-fab-text text-[12.5px] font-semibold disabled:opacity-60"
                data-testid="payment-qr-remove"
              >
                Remove
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            className="hidden"
            onChange={onFile}
            data-testid="payment-qr-file-input"
          />
        </div>
      </div>
    </div>
  );
}
